import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Clock } from 'lucide-react';
import dashStyles from '../../pages/admin/AdminDashboardPage.module.css';
import { fmtCosto, fmtNumero } from '../../utils/usoIaFormato';
import { etiquetaHora, ultimasHoras, type ServicioUsoDoc } from './costosUtil';
import TooltipGrafico from './TooltipGrafico';
import styles from './ServiciosUsoPanel.module.css';

interface Props {
  docs: ServicioUsoDoc[];
  cargando: boolean;
  error: boolean;
}

/** Vista «Por hora, últimas 48 h»: costo medido y minutos de participante de LiveKit. */
export default function ServiciosPorHora({ docs, cargando, error }: Props) {
  const { datos, hayCosto, hayMinutos } = useMemo(() => {
    const slots = ultimasHoras(48);
    const costo = new Map<string, number>();
    const minutos = new Map<string, number>();
    for (const d of docs) {
      if (d.costo != null && Number.isFinite(d.costo)) costo.set(d.periodo, (costo.get(d.periodo) ?? 0) + d.costo);
      if (d.proveedor === 'livekit' && d.servicio === 'participante_minutos') {
        minutos.set(d.periodo, (minutos.get(d.periodo) ?? 0) + (Number(d.cantidad) || 0));
      }
    }
    const filas = slots.map((p) => ({
      periodo: p,
      hora: `${p.slice(11, 13)}h`,
      Costo: costo.has(p) ? Number(costo.get(p)!.toFixed(4)) : undefined,
      Minutos: minutos.has(p) ? Number(minutos.get(p)!.toFixed(2)) : undefined,
    }));
    return { datos: filas, hayCosto: costo.size > 0, hayMinutos: minutos.size > 0 };
  }, [docs]);

  const tick = (v: string) => v;
  const titulo = (hora: string) => {
    const f = datos.find((x) => x.hora === hora);
    return f ? etiquetaHora(f.periodo) : hora;
  };

  return (
    <div className={dashStyles.section}>
      <div className={dashStyles.sectionHeader}>
        <h2 className={dashStyles.sectionTitle}><Clock size={16} aria-hidden="true" /> Por hora, últimas 48 h</h2>
        <span className={dashStyles.sectionBadge}>hora de Panamá</span>
      </div>
      <div className={dashStyles.sectionBody}>
        {cargando ? (
          <p className={dashStyles.emptyState}>Cargando…</p>
        ) : error ? (
          <p className={styles.errorBox} role="alert">No se pudo leer el consumo por hora.</p>
        ) : !hayCosto && !hayMinutos ? (
          <p className={dashStyles.emptyState}>Aún no hay datos por hora de estos servicios: se llenan cada hora.</p>
        ) : (
          <div className={dashStyles.twoCol}>
            <figure style={{ margin: 0, minWidth: 0 }}>
              <figcaption className={styles.subtitulo} style={{ marginTop: 0 }}>Costo medido por hora</figcaption>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={datos} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                  <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={3} tickFormatter={tick} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCosto(v)} width={62} />
                  <Tooltip content={<TooltipGrafico formato={(_n, v) => fmtCosto(v)} titulo={titulo} />} cursor={{ fill: 'var(--color-surface-muted)' }} />
                  <Bar dataKey="Costo" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </figure>
            <figure style={{ margin: 0, minWidth: 0 }}>
              <figcaption className={styles.subtitulo} style={{ marginTop: 0 }}>Minutos de participante (LiveKit)</figcaption>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={datos} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                  <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={3} tickFormatter={tick} />
                  <YAxis tick={{ fontSize: 11 }} width={46} allowDecimals={false} />
                  <Tooltip content={<TooltipGrafico formato={(_n, v) => `${fmtNumero(v, 1)} min`} titulo={titulo} />} cursor={{ fill: 'var(--color-surface-muted)' }} />
                  <Bar dataKey="Minutos" fill="#6366f1" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </figure>
          </div>
        )}
        <p className={styles.muted} style={{ marginTop: 'var(--space-3)' }}>
          El costo por hora suma lo que se mide con precio (Google estimado, Deepgram…). La cuota y el excedente
          de LiveKit se calculan por día, así que no aparecen aquí; los minutos sí.
        </p>
      </div>
    </div>
  );
}
